import { Link } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { MapPin, Users, Star, ArrowRight, Heart } from 'lucide-react';

const PROPERTY_IMAGES = [
  "https://images.pexels.com/photos/35264654/pexels-photo-35264654.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  "https://images.unsplash.com/photo-1572177215152-32f247303126?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1765775635143-6462630748ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBob3RlbCUyMGludGVyaW9yJTIwcm9vbXxlbnwwfHx8fDE3NzU0ODA5MjR8MA&ixlib=rb-4.1.0&q=85",
];

export function FeaturedProperties({ properties, propertyRatings, favoriteIds, onToggleFavorite, brandData }) {
  const { t, locale, formatPrice } = useI18n();

  const bt = (brandField, i18nKey, params) => {
    if (locale === 'es' && brandField) return brandField;
    return t(i18nKey, params);
  };

  return (
    <section className="py-20 bg-[#F5F4F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-12">
          <div>
            <p className="font-semibold uppercase tracking-[0.2em] text-xs mb-3" style={{ color: 'var(--brand-secondary, #C36F4D)' }}>
              {bt(brandData?.featured_label, 'landing.featured')}
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-brand-primary" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
              {bt(brandData?.featured_title, 'landing.featured.title')}
            </h2>
          </div>
          <Link to="/properties">
            <Button variant="outline" className="hidden sm:flex border-[#264639] text-[#264639] hover:bg-[#DCE9E2]">
              {t('landing.featured.viewAll') || 'Ver todos'} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {properties.map((property, index) => (
            <Link
              key={property.property_id}
              to={`/properties/${property.property_id}`}
              className="group"
              data-testid={`property-card-${property.property_id}`}
            >
              <div className="bg-white rounded-xl overflow-hidden shadow-sm card-hover">
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={property.image_url || PROPERTY_IMAGES[index % PROPERTY_IMAGES.length]}
                    alt={property.title}
                    className="w-full h-full object-cover property-image"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-sm font-medium text-[#264639]">
                      {property.property_type || 'Hotel'}
                    </span>
                  </div>
                  <button
                    onClick={(e) => onToggleFavorite(e, property.property_id)}
                    className="absolute top-4 right-4 w-9 h-9 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-sm hover:bg-white transition-all hover:scale-110"
                    data-testid={`fav-btn-${property.property_id}`}
                  >
                    <Heart className={`w-4.5 h-4.5 transition-colors ${favoriteIds.has(property.property_id) ? 'text-[#C36F4D] fill-[#C36F4D]' : 'text-[#57534E]'}`} />
                  </button>
                </div>
                <div className="p-5">
                  <div className="flex items-center text-[#78716C] text-xs mb-1 uppercase tracking-wider">
                    {property.property_type || t('landing.featured.accommodation')}
                  </div>
                  <div className="flex items-center text-[#78716C] text-sm mb-2">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span className="truncate">{property.city}{property.country ? `, ${property.country}` : ''}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-[#1C1917] mb-2 line-clamp-2" style={{ fontFamily: 'var(--font-heading, Outfit)' }}>
                    {property.title}
                  </h3>
                  <div className="flex items-center text-[#57534E] text-sm mb-2">
                    <Users className="w-4 h-4 mr-1" />
                    {property.guests} {t('common.guests')}
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    {property.price_min_usd && (
                      <p className="font-semibold text-brand-accent">
                        {t('landing.featured.from')} {formatPrice(property.price_min_usd)} <span className="text-[#78716C] font-normal text-sm">{t('common.perNight')}</span>
                      </p>
                    )}
                    {propertyRatings[property.property_id] && (
                      <div className="flex items-center gap-1">
                        <Star className="w-3.5 h-3.5 text-[#C36F4D] fill-[#C36F4D]" />
                        <span className="text-sm font-medium text-[#1C1917]">{propertyRatings[property.property_id].avg_rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                  <span className="inline-flex items-center text-sm font-medium mt-2 text-brand-accent">
                    {t('landing.featured.viewProperty')} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8 sm:hidden">
          <Link to="/properties">
            <Button variant="outline" className="border-[#264639] text-[#264639] hover:bg-[#DCE9E2]">
              {t('landing.featured.viewAllMobile')} <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}
